// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Stack, Tab } from '@mui/material';
// Components, Layouts, Pages
import ChildrenPage from '~/pages/common/childrenPage/ChildrenPage';
import { BaseVerticalTab, Loading, UnderDevelopment } from '~/components/common';
// Others
import {
  BACK_TO_PREVIOUS_PAGE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { convertDataUpdate, defaultValuesForm, schema } from './helper';
import {
  dataTimeZone,
  getCurrentDateUTC,
  getEndTimeOption,
  getNextTimeOption,
  removeEmptyObjects,
} from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import {
  IBodyUpdateCustomer,
  IBodyUpdateJob,
  IBodyUpdateJobFields,
  IBodyUpdateJobTags,
  IBodyUpdateLeadSource,
  IBodyUpdatePrivateNotes,
  IBodyUpdateSchedule,
  IDiscountJob,
  IFormJob,
  IJobDetailUpdateReq,
  IMaterialsJob,
  IPhotoAttachmentJob,
  IServiceJob,
  ITaskJob,
  IUpdateJobBody,
} from '~/utils/interface/job';
import { createJob, getJobDetailUpdate, updateJob } from '~/thunks/job/jobThunk';
import { CreateJobTabsEnum, TypeTabCreateJobEnum } from '~/utils/enum/common';
import { IBaseTabItem } from '~/utils/interface/common';
import {
  CreateJobCustomerTab,
  CreateJobLineItemTab,
  CreateJobPrivateNotesTab,
  CreateJobScheduleTab,
  CreateJobFieldsTab,
  CreateJobTagsTab,
  CreateJobLeadSourceTab,
  CreateJobTaskTab,
  CreateJobAttachmentTab,
} from '~/components/module/job';
import { varColor } from '~/themes/themeVariables';
// Styles, images, icons
import styles from './FormJobTemplate.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const FormJobTemplate = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/job', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: jobId } = useParams();
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('customerId');

  const methods = useForm<IFormJob>({
    resolver: yupResolver(schema(t)),
    defaultValues: {
      ...defaultValuesForm,
      startDate: getCurrentDateUTC(),
      endDate: getCurrentDateUTC(),
      startTime: getNextTimeOption(),
      endTime: getEndTimeOption(getNextTimeOption()),
    },
  });
  const {
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { isDirty },
  } = methods;
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [tabKey, setTabKey] = useState<string>(CreateJobTabsEnum.CUSTOMER);
  const [tabItems, setTabItems] = useState<IBaseTabItem[]>([]);

  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [initialData, setInitialData] = useState<IFormJob>();

  // const [employeeSearchKey, setEmployeeSearchKey] = useState<string>(EMPTY_STRING);
  // const [customerSearchKey, setCustomerSearchKey] = useState<string>(EMPTY_STRING);
  // const [referralBySearchKey, setReferralBySearchKey] = useState<string>(EMPTY_STRING);
  // const debounceReferralBySearchKey = useDebounce(referralBySearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  // const debounceEmployeeSearchKey = useDebounce(employeeSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  // const debounceCustomerSearchKey = useDebounce(customerSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  const getMenuItems = useCallback(() => {
    const items: Array<IBaseTabItem> = [
      {
        label: CreateJobTabsEnum.CUSTOMER,
        component: <CreateJobCustomerTab onEdit={handleEditJob} onCancelEdit={handleCancelFormEditJob} />,
        icon: icons.createJobTabIconCustomer,
      },
    ];

    items.push({
      label: CreateJobTabsEnum.SCHEDULE,
      component: <CreateJobScheduleTab onEdit={handleEditJob} onCancelEdit={handleCancelFormEditJob} />,
      icon: icons.createJobTabIconSchedule,
    });

    items.push({
      label: CreateJobTabsEnum.LINE_ITEMS,
      component: <CreateJobLineItemTab onEdit={handleEditJob} onCancelEdit={handleCancelFormEditJob} />,
      icon: icons.createJobTabIconSchedule,
    });

    items.push({
      label: CreateJobTabsEnum.PRIVATE_NOTES,
      component: <CreateJobPrivateNotesTab onEdit={handleEditJob} onCancelEdit={handleCancelFormEditJob} />,
      icon: icons.createJobTabIconPrivateNotes,
    });

    items.push({
      label: CreateJobTabsEnum.TASK,
      component: <CreateJobTaskTab onEdit={handleEditJob} onCancelEdit={handleCancelFormEditJob} />,
      icon: icons.createJobTabIconTask,
    });

    items.push({
      label: CreateJobTabsEnum.JOB_FIELDS,
      component: <CreateJobFieldsTab onEdit={handleEditJob} onCancelEdit={handleCancelFormEditJob} />,
      icon: icons.createJobTabIconJobFields,
    });

    items.push({
      label: CreateJobTabsEnum.JOB_TAGS,
      component: <CreateJobTagsTab onEdit={handleEditJob} onCancelEdit={handleCancelFormEditJob} />,
      icon: icons.createJobTabIconTags,
    });

    items.push({
      label: CreateJobTabsEnum.LEAD_SOURCE,
      component: <CreateJobLeadSourceTab onEdit={handleEditJob} onCancelEdit={handleCancelFormEditJob} />,
      icon: icons.createJobTabIconLeadSource,
    });

    items.push({
      label: CreateJobTabsEnum.ATTACHMENT,
      component: <CreateJobAttachmentTab />,
      icon: icons.createJobTabIconAttachment,
    });

    setTabItems(items);

    if (!jobId) return;

    // TODO: Handle get job detail
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, initialData]);

  useEffect(() => {
    getMenuItems();
  }, [getMenuItems]);

  useEffect(() => {
    if (!customerId) return;
    setValue('clientId', customerId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  useEffect(() => {
    if (!jobId) return;

    handleGetJobDetail(jobId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => {
    if (!jobId) return;

    reset(initialData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetJobDetail = (id: string) => {
    setIsLoading(true);

    const payload: IJobDetailUpdateReq = {
      jobId: id,
      params: {
        timeZone: dataTimeZone(),
      },
    };

    dispatch(getJobDetailUpdate(payload))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const jobDetail = res?.data;

        const assigneeIds: string[] = jobDetail?.schedule?.assignee?.map((assignee) => assignee?.id) || [];
        const jobTagIds: string[] = jobDetail?.jobTags?.map((tag) => tag?.id) || [];

        const newData: IFormJob = {
          name: jobDetail?.name || EMPTY_STRING,
          assigneeIds: assigneeIds,
          commissionRate: jobDetail?.schedule?.commissionRate || DEFAULT_NUMBER_ZERO,
          address: jobDetail?.address || EMPTY_STRING,
          city: jobDetail?.city || EMPTY_STRING,
          state: jobDetail?.state || EMPTY_STRING,
          zipCode: jobDetail?.zipCode || EMPTY_STRING,
          country: jobDetail?.country || EMPTY_STRING,
          latitude: jobDetail?.latitude || DEFAULT_NUMBER_ZERO,
          longitude: jobDetail?.longitude || DEFAULT_NUMBER_ZERO,
          clientId: jobDetail?.client?.id || EMPTY_STRING,
          type: jobDetail?.jobFields?.type || EMPTY_STRING,
          leadSourceId: jobDetail?.leadSource?.id || EMPTY_STRING,
          jobTagIds: jobTagIds,
          jobServices:
            jobDetail?.lineItems?.services?.map((service: IServiceJob) => {
              return {
                id: service?.id || EMPTY_STRING,
                serviceId: service?.serviceId || EMPTY_STRING,
                price: service?.price?.toString() || EMPTY_STRING,
                quantity: service?.quantity?.toString() || EMPTY_STRING,
                totalPrice: service?.totalPrice?.toString() || EMPTY_STRING,
                description: service?.description || EMPTY_STRING,
                cost: service?.cost?.toString() || EMPTY_STRING,
              };
            }) || [],
          jobMaterials:
            jobDetail?.lineItems?.materials?.map((material: IMaterialsJob) => {
              return {
                id: material?.id || EMPTY_STRING,
                materialId: material?.materialId || EMPTY_STRING,
                price: material?.price?.toString() || EMPTY_STRING,
                quantity: material?.quantity?.toString() || EMPTY_STRING,
                totalPrice: material?.totalPrice?.toString() || EMPTY_STRING,
                description: material?.description || EMPTY_STRING,
                cost: material?.cost?.toString() || EMPTY_STRING,
              };
            }) || [],
          discounts:
            jobDetail?.lineItems?.discounts?.map((discount) => ({
              ...discount,
              rate: discount?.rate?.toString() || EMPTY_STRING,
              description: discount?.description || EMPTY_STRING,
            })) || [],
          startDate: jobDetail?.schedule?.startDate || EMPTY_STRING,
          startTime: jobDetail?.schedule?.startTime || EMPTY_STRING,
          endDate: jobDetail?.schedule?.endDate || EMPTY_STRING,
          endTime: jobDetail?.schedule?.endTime || EMPTY_STRING,
          callback: jobDetail?.jobFields?.callBack || false,
          unit: jobDetail?.jobFields?.unit || EMPTY_STRING,
          notes: jobDetail?.privateNotes?.content || EMPTY_STRING,
          privateNotesId: jobDetail?.privateNotes?.id || EMPTY_STRING,
          tasks:
            jobDetail?.tasks?.map((item) => ({
              id: item?.id,
              title: item?.title,
              assigneeId: item?.assignee?.id,
              dueDate: item?.dueDate,
              description: item?.description,
              status: item?.status,
              assignee: item?.assignee,
            })) || [],
          folders:
            jobDetail.folders?.map((folder: IPhotoAttachmentJob) => {
              return {
                name: folder.name,
                isCurrentApply: folder.isCurrentApply,
              };
            }) || [],
        };

        setInitialData(newData);
        reset(newData);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleCancelFormJob = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };

  const handleCancelFormEditJob = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };

  const handleAddJob = async (data: IFormJob) => {
    setIsLoading(true);

    const body: IFormJob = removeEmptyObjects(data) as IFormJob;

    dispatch(createJob({ ...body, ...(data.assigneeIds?.length && { assigneeIds: data.assigneeIds }) }))
      .unwrap()
      .then(() => {
        handleCancelFormJob();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditJob = (data: IFormJob, type: string) => {
    const { clientId, name, address, ...restData } = data;
    let result: Partial<IBodyUpdateJob> = {};

    switch (type) {
      case TypeTabCreateJobEnum.CUSTOMER:
        const dataCustomer: IBodyUpdateCustomer = {
          clientId: clientId,
          address: address,
          address2: restData.address2 || EMPTY_STRING,
          city: restData.city || EMPTY_STRING,
          state: restData.state || EMPTY_STRING,
          zipCode: restData.zipCode || EMPTY_STRING,
          country: restData.country || EMPTY_STRING,
        };

        result = {
          name: name,
          customer: dataCustomer,
        };
        break;

      case TypeTabCreateJobEnum.SCHEDULE:
        const dataSchedule: IBodyUpdateSchedule = {
          startDate: restData.startDate || EMPTY_STRING,
          endDate: restData.endDate || EMPTY_STRING,
          startTime: restData.startTime || EMPTY_STRING,
          endTime: restData.endTime || EMPTY_STRING,
          assigneeIds: restData.assigneeIds || [],
          commissionRate: restData.commissionRate || DEFAULT_NUMBER_ZERO,
        };

        result = {
          schedule: dataSchedule,
        };
        break;

      case TypeTabCreateJobEnum.LINE_ITEMS:
        const {
          updated: updatedServices,
          created: createdServices,
          deleted: deletedServices,
        } = convertDataUpdate<IServiceJob>(restData.jobServices, initialData?.jobServices ?? []);

        const {
          updated: updatedMaterials,
          created: createdMaterials,
          deleted: deletedMaterials,
        } = convertDataUpdate<IMaterialsJob>(restData.jobMaterials, initialData?.jobMaterials ?? []);

        const {
          updated: updatedDiscounts,
          created: createdDiscounts,
          deleted: deletedDiscounts,
        } = convertDataUpdate<IDiscountJob>(restData.discounts, initialData?.discounts ?? []);

        result = {
          lineItems: {
            jobServices: {
              updatedServices,
              createdServices,
              deletedServices,
            },
            jobMaterials: {
              updatedMaterials,
              createdMaterials,
              deletedMaterials,
            },
            discounts: {
              updatedDiscounts,
              createdDiscounts,
              deletedDiscounts,
            },
            subtotal: restData.subtotal,
            total: restData.total,
          },
        };
        break;

      case TypeTabCreateJobEnum.PRIVATE_NOTES:
        const privateNote: IBodyUpdatePrivateNotes = {
          id: restData.privateNotesId || EMPTY_STRING,
          content: restData.notes || EMPTY_STRING,
        };

        result = {
          privateNotes: privateNote,
        };
        break;

      case TypeTabCreateJobEnum.TASK:
        const {
          updated: updatedTasks,
          created: createdTasks,
          deleted: deletedTasks,
        } = convertDataUpdate<ITaskJob>(restData.tasks ?? [], initialData?.tasks ?? []);

        result = {
          tasks: {
            updatedTasks,
            createdTasks,
            deletedTasks,
          },
        };
        break;

      case TypeTabCreateJobEnum.JOB_FIELDS:
        const jobFields: IBodyUpdateJobFields = {
          type: restData.type || EMPTY_STRING,
          unit: restData.unit || EMPTY_STRING,
          callback: restData.callback || false,
        };

        result = {
          jobFields: jobFields,
        };
        break;

      case TypeTabCreateJobEnum.JOB_TAGS:
        const jobTags: IBodyUpdateJobTags = {
          masterDataIds: restData.jobTagIds || [],
        };

        result = {
          jobTags: jobTags,
        };
        break;

      case TypeTabCreateJobEnum.LEAD_SOURCE:
        const leadSource: IBodyUpdateLeadSource = {
          leadSourceId: restData.leadSourceId || EMPTY_STRING,
        };

        result = {
          leadSource: leadSource,
        };
        break;
    }

    if (!jobId) return;

    const payload: IUpdateJobBody = {
      jobId,
      body: {
        ...result,
        sectionType: type,
      },
    };

    setIsLoading(true);

    dispatch(updateJob(payload))
      .unwrap()
      .then((res) => {
        handleCancelFormJob();
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSubmitFormJob = (data: IFormJob) => {
    const newData: IFormJob = { ...data, tasks: data?.tasks?.map(({ status, ...rest }) => rest) };

    if (!jobId) {
      handleAddJob(newData);
    }
  };

  const renderTabContent = () => {
    const item = tabItems.find((tabItem: IBaseTabItem) => {
      return tabKey === tabItem.label;
    });

    return item?.component;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabKey(newValue);
  };
  //#endregion Handle Function

  return (
    <ChildrenPage
      id='formJobPage'
      title={jobId ? t('edit_job_title') : t('form_job_title_add')}
      onBackToPreviousPage={handleCancelFormJob}
    >
      <>
        <FormProvider {...methods}>
          <Stack
            component={'form'}
            spacing={'20px'}
            className={cx('form')}
            onSubmit={handleSubmit(handleSubmitFormJob)}
          >
            {!jobId && (
              <Stack
                direction={'row'}
                alignItems={'center'}
                justifyContent={'flex-end'}
                component={'div'}
                spacing={'12px'}
                className={cx('formFooter')}
              >
                <Button variant='outlined' onClick={handleCancelFormJob}>
                  {t('common_action_cancel', { ns: 'common/base' })}
                </Button>

                <Button variant='contained' type='submit' disabled={jobId ? !isDirty : false}>
                  {t('common_action_save', { ns: 'common/base' })}
                </Button>
              </Stack>
            )}

            <Box component={'div'} className={cx('formBody')}>
              <Box component={'div'} className={cx('viewVerticalTab')}>
                <BaseVerticalTab value={tabKey} onChange={handleTabChange}>
                  {tabItems.map((item, index) => (
                    <Tab
                      id='verticalTabItemComponent'
                      value={item.label || EMPTY_STRING}
                      sx={{
                        border: `1px solid ${
                          item.label === tabKey ? varColor.primary : varColor.tabItemBorder
                        }`,
                      }}
                      label={
                        <Stack
                          direction={'row'}
                          spacing={1}
                          alignItems={'center'}
                          justifyContent={'space-between'}
                          style={{ width: '100%' }}
                        >
                          <Stack
                            direction={'row'}
                            spacing={1}
                            alignItems={'center'}
                            className={cx('labelGroup')}
                          >
                            <img
                              src={item.icon}
                              alt={t('common_alt_img', { ns: 'common/base' })}
                              className={cx('tabIcon')}
                            />

                            <span className={cx('tabLabel')}>
                              {t(`tab_label_${item.label}`) || EMPTY_STRING}
                            </span>
                          </Stack>

                          <img
                            src={icons.tabIconPlus}
                            alt={t('common_alt_img', { ns: 'common/base' })}
                            className={cx('tabIcon')}
                          />
                        </Stack>
                      }
                      className={cx('tabItem', { active: tabKey === item.label })}
                      key={index}
                    />
                  ))}
                </BaseVerticalTab>
              </Box>

              <Box component={'div'} className={cx('viewContent')}>
                {renderTabContent()}
              </Box>
            </Box>
          </Stack>
        </FormProvider>

        {isLoading && <Loading />}

        <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
      </>
    </ChildrenPage>
  );
};

export default FormJobTemplate;
